import { AnimationGroup, Color3, Mesh, PBRMaterial, Quaternion, StandardMaterial, TransformNode, Vector3 } from '@babylonjs/core';
import { Level } from './Level';
import { MeshFactory } from './MeshFactory';

export class NetworkPlayer {
  private readonly verticalVisualOffset = 0.12;
  private readonly playerHeight = 1.8;
  private assetRotation = new Vector3(Math.PI, Math.PI, 0);

  public model!: TransformNode;
  public displayMesh!: Mesh;

  private currentAnimName: string | null = null;
  private targetPosition: Vector3 = Vector3.Zero();
  private targetRotation: Quaternion = Quaternion.Identity();
  private animations: Map<string, AnimationGroup> = new Map();

  private readonly nonLoopingAnimations = ['Dying', 'RunningSlide', 'RoundKick', 'Throw', 'FallingToLanding'];

  constructor(private level: Level) {
    this.model = new TransformNode('networkPlayerRoot', this.level.scene);
    this.model.position = new Vector3(0, 10, 0); // initial offscreen
    this.model.rotationQuaternion = Quaternion.Identity();
  }

  async init() {
    if (!this.level.player || !this.level.player.displayMesh) {
      console.warn('NetworkPlayer requires Player to be initialized first.');
      return;
    }

    const sourceMesh = this.level.player.displayMesh;
    const asset = this.level.game.assetFactory.getAsset('player');
    const skeleton = asset?.getSkeleton() || null;
    const animations = asset?.getAnimationGroups() || [];

    // Clone the local player's mesh directly to ensure 1:1 geometry and visibility without side effects
    const { mesh: clonedMesh, animations: clonedAnimations } = MeshFactory.cloneMesh(
      sourceMesh,
      skeleton,
      animations,
      'network_player',
      'clone'
    );

    this.displayMesh = clonedMesh as Mesh;
    this.displayMesh.parent = this.model;

    // We inherit scaling and rotationQuaternion directly from the clone,
    // so we only need to set the position offset relative to our model node
    this.displayMesh.position = new Vector3(0, -this.playerHeight / 2 - this.verticalVisualOffset, 0);

    this.level.addShadowCaster(this.displayMesh);
    this.displayMesh.receiveShadows = true;

    // Map animations for the network player
    if (clonedAnimations) {
      clonedAnimations.forEach((anim) => {
        // The cloned animations will have names like "network_player.Idle"
        const originalName = anim.name.replace('network_player.', '');
        this.animations.set(originalName, anim);
        anim.enableBlending = true;
        anim.blendingSpeed = 0.1;
      });
    }

    // Apply a tint to distinguish the network player
    this.applyNetworkTint();
  }

  private applyNetworkTint() {
    // Check the main mesh and all its children
    const meshes = this.displayMesh.getChildMeshes(false);
    if (this.displayMesh instanceof Mesh) {
      meshes.push(this.displayMesh);
    }

    meshes.forEach((child) => {
      if (child instanceof Mesh && child.material) {
        // Clone the material to avoid affecting other players/instances
        const newMat = child.material.clone(child.material.name + '_network');

        if (newMat instanceof PBRMaterial) {
          newMat.albedoColor = new Color3(0.2, 0.2, 1.0); // Stronger blue tint
          newMat.emissiveColor = new Color3(0.1, 0.1, 0.4); // Subtle glow
          newMat.emissiveIntensity = 0.5;
        } else if (newMat instanceof StandardMaterial) {
          newMat.diffuseColor = new Color3(0.2, 0.2, 1.0);
          newMat.emissiveColor = new Color3(0.1, 0.1, 0.4);
        }

        child.material = newMat;
      }
    });
  }

  updateState(
    position: { x: number; y: number; z: number },
    rotation: { x: number; y: number; z: number; w: number },
    animationName: string | undefined
  ) {
    this.targetPosition.set(position.x, position.y, position.z);
    this.targetRotation.set(rotation.x, rotation.y, rotation.z, rotation.w);

    // Animation sync
    if (animationName && animationName !== this.currentAnimName) {
      if (this.currentAnimName && this.animations.has(this.currentAnimName)) {
        this.animations.get(this.currentAnimName)!.stop();
      }
      this.currentAnimName = animationName;
      const anim = this.animations.get(animationName);
      if (anim) {
        const shouldLoop = !this.nonLoopingAnimations.includes(animationName);
        anim.play(shouldLoop);
      }
    }
  }

  update(deltaTime: number) {
    if (!this.displayMesh) return;

    // Interpolate position and rotation
    this.model.position = Vector3.Lerp(this.model.position, this.targetPosition, 0.3);

    if (!this.model.rotationQuaternion) {
      this.model.rotationQuaternion = Quaternion.Identity();
    }

    Quaternion.SlerpToRef(this.model.rotationQuaternion, this.targetRotation, 0.3, this.model.rotationQuaternion);
  }

  dispose() {
    this.model.dispose();
  }
}
